import React, { useEffect, useState } from 'react';
import JobApplicationForm from '../components/JobApplicationForm';
import API_BASE from '../utils/apiBase';
import { getLocalJobs } from '../utils/jobStorage';

const getJobFileUrl = (job) => {
  const url = job.details_file_url || job.details_file;
  if (!url) return null;
  if (url.startsWith('data:') || url.startsWith('blob:') || url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  const cleanPath = url.startsWith('/') ? url : `/${url}`;
  const BASE_URL = (process.env.REACT_APP_API_BASE || "https://palegreen-snake-884644.hostingersite.com/backend").replace(/\/api$/, "");
  return `${BASE_URL}${cleanPath}`;
};

const isImageFile = (job) => {
  const url = job.details_file_url || job.details_file || '';
  const name = job.details_file_name || '';
  if (url.startsWith('data:image/')) return true;
  return /\.(png|jpg|jpeg|gif|webp)$/i.test(name) || /\.(png|jpg|jpeg|gif|webp)$/i.test(url);
};

const handleJobFileDownload = (job, e) => {
  const url = getJobFileUrl(job);
  const fileName = job.details_file_name || `${job.title ? job.title.replace(/\s+/g, '_') : 'Job'}_Details`;
  if (!url) return;
  if (url.startsWith('data:')) {
    e.preventDefault();
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

const Career = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImageModal, setActiveImageModal] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/api/jobs.php`)
      .then(async (res) => {
        if (!res.ok) throw new Error('HTTP error');
        const text = await res.text();
        return JSON.parse(text);
      })
      .then(data => {
        if (data && Array.isArray(data.jobs) && data.jobs.length > 0) {
          setJobs(data.jobs);
        } else {
          setJobs(getLocalJobs());
        }
        setLoading(false);
      })
      .catch(() => {
        setJobs(getLocalJobs());
        setLoading(false);
      });
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-black to-gray-900 py-10 px-2 md:px-8 flex items-center justify-center" style={{ marginTop: "90px" }}>
      <div className="max-w-6xl w-full grid grid-cols-1 md:grid-cols-2 gap-10">
        {/* Form Section */}
        <div className="bg-black/20 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-8 flex flex-col justify-center">
          <h2 className="text-3xl font-bold text-white mb-6 text-center drop-shadow">Join Us</h2>
          <JobApplicationForm jobs={jobs} />
        </div>
        {/* Job Listings Section */}
        <div className="bg-black/20 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-8">
          <h2 className="text-3xl font-bold text-white mb-6 text-center drop-shadow">Open Positions</h2>
          {loading ? (
            <div className="text-center text-blue-200">Loading...</div>
          ) : jobs.length === 0 ? (
            <div className="text-center text-gray-400">No positions available.</div>
          ) : (
            <div className="space-y-6">
              {jobs.map(job => {
                const docUrl = getJobFileUrl(job);
                const isImg = isImageFile(job);
                const docName = job.details_file_name || `${job.title ? job.title.replace(/\s+/g, '_') : 'Job'}_Details`;
                return (
                  <div
                    className="bg-black/30 hover:bg-black/40 transition rounded-xl p-6 shadow-md flex flex-col gap-2 border border-white/10"
                    key={job.id}
                  >
                    <h3 className="text-xl font-semibold text-white mb-1 drop-shadow">{job.title}</h3>
                    <div className="text-gray-200 text-sm mb-2" dangerouslySetInnerHTML={{__html: job.description}} />
                    <div className="flex flex-wrap gap-4 text-sm text-blue-100 mb-2">
                      <span><span className="font-medium">Qualification:</span> {job.qualification}</span>
                      <span><span className="font-medium">Experience:</span> {job.experience}</span>
                      <span><span className="font-medium">Positions:</span> {job.positions}</span>
                    </div>
                    {docUrl && (
                      <div className="pt-3 border-t border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <span className="text-xs text-gray-300 font-medium flex items-center gap-1">
                          {isImg ? "🖼️ Job Specification Image:" : "📄 Job Specification Document:"}
                        </span>
                        {isImg ? (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setActiveImageModal({ url: docUrl, title: job.title })}
                              className="bg-slate-800 hover:bg-slate-700 text-sky-300 text-xs font-semibold px-3 py-1.5 rounded-lg border border-sky-500/30 transition flex items-center gap-1"
                            >
                              <span>🔍</span> View Image
                            </button>
                            <a
                              href={docUrl}
                              download={docName}
                              onClick={(e) => handleJobFileDownload(job, e)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg shadow transition flex items-center gap-1"
                            >
                              <span>📥</span> Download
                            </a>
                          </div>
                        ) : (
                          <a
                            href={docUrl}
                            download={docName}
                            onClick={(e) => handleJobFileDownload(job, e)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3.5 py-1.5 rounded-lg shadow transition flex items-center gap-1.5"
                          >
                            <span>📥</span> Download Document ({docName.length > 18 ? `${docName.slice(0, 15)}...` : docName})
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Lightbox Modal for Image Specification */}
      {activeImageModal && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setActiveImageModal(null)}
        >
          <div
            className="relative bg-slate-900 border border-white/20 rounded-2xl p-4 max-w-3xl w-full flex flex-col items-center max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-full flex justify-between items-center mb-3 px-2">
              <h3 className="text-lg font-bold text-white">{activeImageModal.title} - Job Details</h3>
              <button
                onClick={() => setActiveImageModal(null)}
                className="text-gray-400 hover:text-white text-xl font-bold px-2"
              >
                ✕
              </button>
            </div>
            <div className="overflow-auto w-full flex items-center justify-center rounded-lg bg-black/40 p-2">
              <img
                src={activeImageModal.url}
                alt="Job Details"
                className="max-h-[70vh] w-auto object-contain rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Career;
